import CostCard from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostCard';
import CostListState from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostListState';
import useAiCostBreakdown from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Hooks/useAiCostBreakdown';
import useAiCostSummaries from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Hooks/useAiCostSummaries';
import ShowMoreButton from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/ShowMoreButton';
import ItemCostCards from '@UI/Pages/Administration/AiCosts/CostTabs/ItemCosts/ItemCostCards';
import ItemCostsTable, {
	type ItemDimension,
} from '@UI/Pages/Administration/AiCosts/CostTabs/ItemCosts/ItemCostsTable';
import SortSwitch from '@UI/Pages/Administration/AiCosts/CostTabs/ItemCosts/SortSwitch';
import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import formatEuros from '@UI/Helpers/formatEuros';
import aiCostsFiltersSignal from '@UI/Pages/Administration/AiCosts/Filters/Signals/aiCostsFiltersSignal';
import formatCount from '@UI/Pages/Administration/AiCosts/Helpers/formatCount';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';

const ROWS_PER_PAGE = 25;

type Props = {
	readonly accountId: number;
	readonly dimension: ItemDimension;
};

const resolveTitle = (dimension: ItemDimension, count: number | null): string => {
	if (dimension === 'documents') {
		if (count === null) {
			return 'Documents générés';
		}

		return count > 1
			? `${formatCount(count)} documents générés`
			: `${formatCount(count)} document généré`;
	}

	if (count === null) {
		return 'Audios traités';
	}

	return count > 1 ? `${formatCount(count)} audios traités` : `${formatCount(count)} audio traité`;
};

const resolveHint = (dimension: ItemDimension, average: number | null): string | null => {
	if (average === null) {
		return null;
	}

	return dimension === 'documents'
		? `Coût moyen sur la période : ${formatEuros(average)} par document.`
		: `Coût moyen sur la période : ${formatEuros(average)} par audio.`;
};

/** Costs per document or per audio, sorted by recency or by cost. Mounted under the query key of its tab. */
const ItemCosts: React.FC<Props> = ({ accountId, dimension }) => {
	const filters = useSignalValue(aiCostsFiltersSignal);
	const { list, error, refetch, showMore, isLoadingMore } = useAiCostBreakdown(
		accountId,
		dimension,
		ROWS_PER_PAGE,
	);
	const { total, reference } = useAiCostSummaries(accountId);
	const isCompact = useIsCompactViewport();
	const part = total === null ? null : dimension === 'documents' ? total.documents : total.audio;

	const actions = React.useMemo(() => <SortSwitch />, []);

	return (
		<CostCard
			title={resolveTitle(dimension, part?.count ?? null)}
			subtitle={
				filters.sort === 'cost'
					? 'Les plus chers en premier : repérez un écart.'
					: 'Les plus récents en premier.'
			}
			actions={actions}
			hint={resolveHint(dimension, part?.averageCost ?? null)}
		>
			<CostListState list={list} error={error} onRetry={refetch}>
				{(loaded) => (
					<React.Fragment>
						{isCompact ? (
							<ItemCostCards
								list={loaded}
								dimension={dimension}
								total={total}
								reference={reference}
							/>
						) : (
							<ItemCostsTable
								list={loaded}
								dimension={dimension}
								total={total}
								reference={reference}
							/>
						)}
						<ShowMoreButton
							hasMore={loaded.hasMore}
							remainingCount={loaded.remainingCount}
							rows={ROWS_PER_PAGE}
							isLoading={isLoadingMore}
							onShowMore={showMore}
						/>
					</React.Fragment>
				)}
			</CostListState>
		</CostCard>
	);
};

export default ItemCosts;
